import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Grid } from '@opendoor/atoms/grid';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { Toasty, useAtomsToastManager } from '@opendoor/atoms/toast';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import * as Sentry from '@sentry/nextjs';
import type {
  AddressSearchResultFragment,
  GetMarketCoordinatesQuery,
  GetMarketplacePropertyListQuery,
  GetMarketplacePropertySearchQuery,
  GetZoneDataQuery,
  MarketplacePropertyListFragment,
  MarketplacePropertySearchFragment,
} from '__generated__/athena/graphql';
import {
  GetBuyerPropertyReactionsDocument,
  OdProtosBuyerV2Data_ReactionSource,
} from '__generated__/athena/graphql';
import { getAthenaClient } from 'components/api';
import { requestZeus } from 'components/api/zeusClient';
import {
  buildMarketInfo,
  fetchActiveBuyMarkets,
  fetchPropertyList,
  fetchPropertySearch,
  parseMarketParam,
  tryGetMarketCoordinates,
  tryGetZoneData,
} from 'components/marketplace/helpers/ssr';
import { useMapRefs } from 'components/marketplace/helpers/useMapRefs';
import { useMarketplaceSearchController } from 'components/marketplace/helpers/useMarketplaceSearchController';
import { usePropertyReactions } from 'components/marketplace/helpers/usePropertyReactions';
import {
  buildAddressPreviewParams,
  buildQueryParams,
  parseQueryParams,
  parseSavedSearchId,
  removeAddressPreviewParams,
} from 'components/marketplace/helpers/useSearchQueries';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';
import { useSearchState } from 'components/marketplace/helpers/useSearchState';
import { MarketplaceMap } from 'components/marketplace/map/MarketplaceMap';
import { MarketplaceMapMarker } from 'components/marketplace/map/MarketplaceMapMarker';
import { MiniPropertyDetailPage } from 'components/marketplace/map/MiniPropertyDetailPage';
import { MobileHeaderFilters } from 'components/marketplace/map/MobileHeaderFilters';
import { MobileMiniPropertyDetailPage } from 'components/marketplace/map/MobileMiniPropertyDetailPage';
import { PropertyList } from 'components/marketplace/map/PropertyList';
import { QuickFilters } from 'components/marketplace/map/QuickFilters';
import { MapSearchFilters } from 'components/marketplace/map/SearchFilters';
import {
  type MapLocation,
  type MarketInfo,
  MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS,
  MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
  type TopMarker,
} from 'components/marketplace/map/types';
import { useMobileMiniPDPSwipeDismiss } from 'components/marketplace/map/useMobileMiniPDPSwipeDismiss';
import { getShapeFromMap } from 'components/marketplace/map/utils';
import { MarketplaceSeoHead } from 'components/marketplace/MarketplaceSeoHead';
import { GetMarketplacePropertyDetailsDocument } from 'components/marketplace/property/details/__generated__/marketplace-property.zeus';
import {
  getPartialMarketplacePropertyDetails,
  mapMarketplaceProperty,
} from 'components/marketplace/property/details/propertyDetails';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { dollarToCentsString, toFilterString } from 'components/marketplace/search/filterHelpers';
import { getZoomForLocationType } from 'components/marketplace/search/utils';
import {
  MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
  SLIDE_PANEL_TRANSITION_CLASS,
} from 'components/marketplace/shared/panelTransitions';
import type { ActiveMarket, PropertyReactions } from 'components/marketplace/types';
import MarketplaceMapLayout from 'components/properties/shared/MarketplaceMapLayout';
import { useSmallerThanLG, useSmallerThanMD } from 'components/shared/hooks/useMediaQuery';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import { useObservability } from 'helpers/observability';
import type { MapInstance, ViewStateChangeEvent } from 'react-map-gl/mapbox';
import { useDebouncedCallback } from 'use-debounce';

// Bridges the search controller's `hasError` flag to the atoms toast manager.
// Must render inside `<Toasty>` so `useAtomsToastManager` resolves. Surfaces the
// error once on each rising edge of `hasError`, then clears it so a later failure
// can re-trigger the toast.
const SearchErrorToast = ({
  hasError,
  clearError,
}: {
  hasError: boolean;
  clearError: () => void;
}) => {
  const toastManager = useAtomsToastManager();
  // `useAtomsToastManager` returns a fresh object each render and `add()` re-renders
  // this subtree, so depending on `toastManager`/`clearError` directly would re-run
  // the effect on every render and loop indefinitely. Read them through refs and
  // key the effect solely on `hasError`.
  const toastManagerRef = useRef(toastManager);
  toastManagerRef.current = toastManager;
  const clearErrorRef = useRef(clearError);
  clearErrorRef.current = clearError;

  useEffect(() => {
    if (!hasError) return;
    toastManagerRef.current.add({
      title: 'We couldn’t update results. Please try again.',
      variant: 'error',
    });
    clearErrorRef.current();
  }, [hasError]);
  return null;
};

// mapbox merges a camera method's `eventData` into every event that method
// fires, so tagging our own `flyTo` lets `handleOnMoveEnd` tell the fly's
// trailing `moveend` apart from a user pan/zoom and skip it — otherwise the fly
// we run to center a freshly selected zone would immediately trigger a viewport
// search that clobbers that zone. This is robust to however many map instances
// echo the fly (and to a no-op fly firing no `moveend`); untagged moves (user
// gestures, the geolocate control) are unaffected and still refetch.
const ZONE_FLY_EVENT_DATA: { odSkipViewportSearch: true } = { odSkipViewportSearch: true };

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  propertyList,
  propertySearch,
  marketInfo,
  queryParams,
  activeMarkets,
  canonicalOverride,
  savedSearchId,
  initialZoneIds,
}) => {
  const { authentication, user } = useAuth();
  const { trackJourneyEvent } = useObservability();
  const isLoggedIn = authentication?.state === 'authenticated';
  const { mapRef } = useMapRefs();
  const isSmallerThanMD = useSmallerThanMD();
  const isSmallerThanLG = useSmallerThanLG();

  const searchState = useSearchState(queryParams);

  // The marketplace search session: result set, loading flag, the location the
  // next search runs against, and the semantic search verbs the handlers call.
  const {
    propertySearchData,
    propertyListData,
    isLoading,
    hasError,
    clearError,
    selectedLocation,
    setSelectedLocation,
    selectedAddress,
    setSelectedAddress,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    loadMore,
    applySavedSearch,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
  } = useMarketplaceSearchController({
    initialSearch: propertySearch,
    initialList: propertyList,
    searchState,
    marketInfo,
    initialVertices: queryParams.vertices,
    initialZoneIds,
    // `ssid` survives the hard navigation a cross-market saved-search apply
    // takes, restoring the active-search state on the destination page.
    initialSavedSearchId: savedSearchId,
  });

  const handleResetFilters = useCallback(() => {
    searchState.resetFilters();
    clearActiveSavedSearch();
  }, [searchState, clearActiveSavedSearch]);

  const {
    minBeds,
    minBaths,
    canSelfTour,
    minPrice,
    maxPrice,
    singleFamily,
    condo,
    townhome,
    noHoas,
    singleStory,
    mustHavePool,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    minSquareFootage,
    maxSquareFootage,
  } = searchState.get;

  // UX states
  const [errors, setErrors] = useState<Record<string, string>>();
  const [showList, setShowList] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showPdp, setShowPdp] = useState<boolean>(false);
  const [propertyReactions, setPropertyReactions] = useState<PropertyReactions | undefined>();

  // Map marker information
  const [topMarker, setTopMarker] = useState<TopMarker | null>(null);
  const [loadingAddressToken, setLoadingAddressToken] = useState<string | null>(null);

  // Individual property details
  const [propertyDetails, setPropertyDetails] = useState<MarketplaceProperty | null>(null);
  const { isLiked, onClickReaction } = usePropertyReactions(
    propertyDetails?.addressToken,
    OdProtosBuyerV2Data_ReactionSource.REACTION_SOURCE_GALLERY,
  );
  const handleFetchPropertyDetails = useCallback(async (addressToken: string) => {
    setLoadingAddressToken(addressToken);
    let property: MarketplaceProperty | null;
    try {
      const result = await requestZeus(GetMarketplacePropertyDetailsDocument, {
        addressId: addressToken,
        includeSimilarListings: false,
      });
      property = mapMarketplaceProperty(result);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-map', operation: 'GetMarketplacePropertyDetails' },
        extra: { addressToken },
      });
      const partialResult = getPartialMarketplacePropertyDetails(error);
      property = partialResult ? mapMarketplaceProperty(partialResult) : null;
    } finally {
      setLoadingAddressToken(null);
    }

    setPropertyDetails(property);
    if (!property) {
      setShowPdp(false);
      return;
    }
    setShowFilters(false);
    setShowPdp(true);
    const urlParams = buildAddressPreviewParams(addressToken);
    window.history.pushState({}, '', `${window.location.pathname}?${urlParams}`);
  }, []);

  useEffect(() => {
    if (queryParams?.preview_aid) {
      handleFetchPropertyDetails(queryParams.preview_aid);
    }
  }, [queryParams?.preview_aid]);

  const flyTo = (location: MapLocation) => {
    const zoom = getZoomForLocationType(location.zoneType);

    mapRef.current?.flyTo({ center: location.coordinates, zoom }, ZONE_FLY_EVENT_DATA);
  };

  const mapMarkers = useMemo(() => {
    setTopMarker(null);
    const selectedAddressToken = propertyDetails?.addressToken;
    const markers = propertySearchData?.results
      ?.slice()
      .sort((a, b) => (b.location?.lat ?? 0) - (a.location?.lat ?? 0))
      ?.map((p) => (
        <MarketplaceMapMarker
          key={p.id}
          property={p}
          topMarker={topMarker}
          handleFetchPropertyDetails={handleFetchPropertyDetails}
          setTopMarker={setTopMarker}
          propertyReactions={propertyReactions}
          isSelected={p.addressToken === selectedAddressToken}
          isLoading={p.addressToken === loadingAddressToken}
        />
      ))
      ?.filter(Boolean);
    return markers ?? [];
  }, [propertySearchData, propertyReactions, propertyDetails?.addressToken, loadingAddressToken]);

  const handleError = (key: string, val: string) => {
    if (val === '') {
      return setErrors((prev) => {
        if (prev && prev[key]) {
          delete prev[key];
          return { ...prev };
        }
        return;
      });
    }

    return setErrors((prev) => ({ ...prev, [key]: val }));
  };

  const getReactions = async () => {
    if (isLoggedIn && user?.customerUuid) {
      const reactions = await getAthenaClient().request(GetBuyerPropertyReactionsDocument, {
        customerId: user.customerUuid,
      });
      const reducedReactions =
        reactions?.buyerService?.getBuyer?.buyer?.reactions?.reduce<PropertyReactions>(
          (reactions, reaction) => {
            reactions[reaction.addressId] = reaction.reaction;
            return reactions;
          },
          {},
        );
      setPropertyReactions(reducedReactions);
    }
  };

  useEffect(() => {
    if (marketInfo?.identifier && !canonicalOverride) {
      const urlParams = buildQueryParams(queryParams);
      window.history.pushState({}, '', `/homes/${marketInfo.identifier}?${urlParams}`);
    }
  }, []);

  useEffect(() => {
    getReactions();
  }, [isLoggedIn]);

  useEffect(() => {
    window.addEventListener('propertyReactionEvent', getReactions);
    return () => window.removeEventListener('propertyReactionEvent', getReactions);
  }, []);

  // Track property view events
  useEffect(() => {
    if (showPdp && propertyDetails) {
      trackJourneyEvent('marketplaceProperty', 'detailPageView', {
        addressUuid: propertyDetails?.addressToken,
      });
    }
  }, [showPdp, propertyDetails]);

  // Close the PDP (if open) when a user taps on the browser's back button
  useEffect(() => {
    const handleBackButton = () => {
      hidePropertyDetails();
      const urlParams = removeAddressPreviewParams();
      window.history.pushState({}, '', `${window.location.pathname}?${urlParams}`);
    };

    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, []);

  // Smaller filter menu on desktop that allows for immediate querying
  const handleQuickFilterChange = async (bedrooms: string, bathrooms: string) => {
    handleSetBedrooms(bedrooms);
    handleSetBathrooms(bathrooms);
    await applyFilters({ bedrooms: Number(bedrooms), bathrooms: Number(bathrooms) });
  };

  // Mobile filter menu CTA click handler to trigger a search, close the filter
  // menu, and fly to the selected location.
  const handleMobileFilterClick = async () => {
    if (selectedLocation) {
      flyTo(selectedLocation);
    }
    await applyFilters();
    setShowFilters(false);
  };

  // Desktop full-filter "Search" CTA. Wraps `applyFilters` so the press event
  // isn't forwarded as a filter override.
  const handleApplyFilters = useCallback(() => {
    void applyFilters();
  }, [applyFilters]);

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const setSearchAreaFromMapBounds = useCallback(
    (map: MapInstance) => applyViewport(getShapeFromMap(map)),
    [applyViewport],
  );

  const hidePropertyDetails = useCallback(() => {
    setShowPdp(false);
  }, []);

  const closePropertyDetails = useCallback(() => {
    setShowPdp(false);
    setPropertyDetails(null);
    const urlParams = removeAddressPreviewParams();
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
  }, []);

  const mobileMiniPdpSwipe = useMobileMiniPDPSwipeDismiss(
    closePropertyDetails,
    showPdp && Boolean(propertyDetails),
  );

  const debouncedSetSearchAreaFromViewport = useDebouncedCallback(setSearchAreaFromMapBounds, 200);

  const handleOnMoveEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      // Skip the `moveend` from our own zone-centering fly (tagged in `flyTo`);
      // a user pan/zoom is untagged and still refetches the viewport.
      if ((e as ViewStateChangeEvent & { odSkipViewportSearch?: boolean }).odSkipViewportSearch) {
        return;
      }
      debouncedSetSearchAreaFromViewport(e.target);
    },
    [debouncedSetSearchAreaFromViewport],
  );

  const handleSetBedrooms = (value: string) => searchState.set.setBedrooms(Number(value));
  const handleSetBathrooms = (value: string) => searchState.set.setBathrooms(Number(value));
  const handleSetCanSelfTour = (value: string) => searchState.set.setCanSelfTour(value === 'true');
  const handleMinPriceChange = (minPrice: string) =>
    searchState.set.setMinPrice(Number(minPrice) / 100);
  const handleMaxPriceChange = (maxPrice: string) =>
    searchState.set.setMaxPrice(Number(maxPrice) / 100);
  const handleSingleFamilyChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleFamily(!!checked);
  const handleCondoChange = (_: string, checked: string | boolean) =>
    searchState.set.setCondo(!!checked);
  const handleTownhomeChange = (_: string, checked: string | boolean) =>
    searchState.set.setTownhome(!!checked);
  const handleNoHoasChange = (_: string, checked: string | boolean) =>
    searchState.set.setNoHoas(!!checked);
  const handleSingleStoryChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleStory(!!checked);
  const handlePoolChange = (_: string, checked: string | boolean) =>
    searchState.set.setMustHavePool(!!checked);
  const handleMinYearBuiltChange = (minYearBuilt: string) =>
    searchState.set.setMinYearBuilt(Number(minYearBuilt));
  const handleMaxYearBuiltChange = (maxYearBuilt: string) =>
    searchState.set.setMaxYearBuilt(Number(maxYearBuilt));
  const handleMinLotSizeChange = (minLotSize: string) =>
    searchState.set.setMinLotSize(Number(minLotSize));
  const handleMaxLotSizeChange = (maxLotSize: string) =>
    searchState.set.setMaxLotSize(Number(maxLotSize));
  const handleMinSquareFootageChange = (min: string) =>
    searchState.set.setMinSquareFootage(Number(min));
  const handleMaxSquareFootageChange = (max: string) =>
    searchState.set.setMaxSquareFootage(Number(max));

  const handleShowList = () => {
    setShowList(true);
  };
  const handleShowMap = () => setShowList(false);
  const onFiltersButtonClick = () => {
    setPropertyDetails(null);
    closePropertyDetails();
    setShowList(false);
    setShowPdp(false);
    setShowFilters(true);
  };

  const handleSetSelectedLocation = async (location?: MapLocation | null) => {
    if (!location) {
      setSelectedLocation(undefined);
      return;
    }
    setIsMobileHeaderFocused(false);
    debouncedSetSearchAreaFromViewport.cancel();
    flyTo(location);
    await applyZoneSearch(location);
  };

  const handleSetSelectedAddress = async (address?: AddressSearchResultFragment | null) => {
    if (!address) {
      setSelectedAddress(undefined);
      return;
    }
    setSelectedAddress(address);
    handleFetchPropertyDetails(address.addressToken);
    setIsMobileHeaderFocused(false);
    const urlParams = buildAddressPreviewParams(address.addressToken);
    window.history.pushState({}, '', `${window.location.pathname}?${urlParams}`);
  };

  const handleOnMobileFilterClick = () => {
    setShowList(false);
    setShowPdp(false);
    setShowFilters(true);
  };

  const [isMobileHeaderFocused, setIsMobileHeaderFocused] = useState(false);

  const handleOnMobileHeaderFocus = () => {
    setIsMobileHeaderFocused(true);
  };

  const handleOnMobileHeaderBlur = () => {
    setIsMobileHeaderFocused(false);
  };

  const RenderBody = (
    <MarketplaceMapLayout>
      <Toasty>
        <Col className="relative h-full w-full overflow-hidden border-t border-od-border-primary bg-od-bg-primary md:flex-row">
          {/** Header */}
          <Row
            onBlur={handleOnMobileHeaderBlur}
            onFocus={handleOnMobileHeaderFocus}
            className={cn(
              'fixed top-1.25 z-[8000] h-15 items-center justify-center transition-[width,left] duration-[250ms] md:hidden',
              isMobileHeaderFocused
                ? 'left-2.5 w-[95%]'
                : 'left-13.75 w-[calc(100%_-_var(--spacing)*50)]',
            )}
          >
            <Col className="w-full">
              <MobileHeaderFilters
                isFocused={isMobileHeaderFocused}
                setSelectedAddress={handleSetSelectedAddress}
                selectedAddress={selectedAddress}
                setSelectedLocation={handleSetSelectedLocation}
                selectedLocation={selectedLocation}
                onBack={handleOnMobileHeaderBlur}
                onOpenFilters={handleOnMobileFilterClick}
              />
            </Col>
          </Row>
          {/** Responsive map — one Mapbox instance for all breakpoints */}
          <Col
            aria-hidden={
              (isSmallerThanMD && (showList || showFilters)) ||
              (!isSmallerThanMD && isSmallerThanLG && showFilters)
            }
            className={cn(
              'relative h-screen w-full touch-none items-center border-t border-od-border-primary transition-[opacity,visibility] duration-500 md:h-full md:basis-1/2 lg:flex-1 lg:basis-1/2',
              (showList || showFilters) && 'max-md:invisible max-md:opacity-0',
              showFilters && 'md:invisible md:opacity-0 lg:visible lg:opacity-100',
            )}
          >
            <MarketplaceMap
              mapRef={mapRef}
              handleOnClick={closePropertyDetails}
              handleOnMoveEnd={handleOnMoveEnd}
              marketShape={marketInfo.shape}
              mapMarkers={mapMarkers}
            />
            <Button
              analyticsName="list-view"
              aria-label="List"
              onClick={handleShowList}
              size="lg"
              variant="secondaryInverse"
              className={cn(
                'fixed bottom-7.5 left-1/2 -translate-x-1/2 md:hidden',
                showList && 'hidden',
              )}
            >
              List
            </Button>
          </Col>
          {/** Fullscreen List (Small) */}
          <Col
            id="small-property-list"
            aria-hidden={!showList}
            className={cn(
              'absolute z-3 h-full w-full items-center overflow-y-scroll bg-od-bg-primary px-5 pb-32.5 md:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showList && 'invisible translate-y-[100vh] opacity-0',
            )}
          >
            <Col className="h-full w-full px-6">
              <Col className="flex-wrap justify-between pb-13">
                <Text as="p" className="text-body-sm py-6">
                  {propertySearchData?.count} homes
                </Text>
                <PropertyList
                  isLoading={isLoading}
                  handleOnSeeMore={loadMore}
                  propertyListData={propertyListData}
                />
                {activeMarkets && activeMarkets.length > 0 && !isLoading && (
                  <Col className="pt-12 pb-27.5">
                    <Text as="p" className="text-body-md pb-6 font-medium">
                      Popular Markets
                    </Text>
                    <Grid className="grid-cols-[repeat(auto-fill,minmax(calc(var(--spacing)*62.5),1fr))] gap-4">
                      <Link
                        href={`/homes-for-sale-near-me`}
                        analyticsName="market-link-near-me"
                        className="block"
                      >
                        <Text as="p" className="text-body-md text-od-content-secondary">
                          Homes for sale near me
                        </Text>
                      </Link>
                      {activeMarkets.map((market) => (
                        <Link
                          key={market.identifier}
                          href={`/homes/${market.identifier}`}
                          analyticsName={`market-link-${market.identifier}`}
                          className="block"
                        >
                          <Text as="p" className="text-body-md text-od-content-secondary">
                            Houses for sale in {market.displayName}
                          </Text>
                        </Link>
                      ))}
                    </Grid>
                  </Col>
                )}
              </Col>
            </Col>
            <Button
              analyticsName="list-view"
              aria-label="Map"
              onClick={handleShowMap}
              size="lg"
              variant="secondaryInverse"
              className={cn('fixed bottom-7.5 left-1/2 -translate-x-1/2', !showList && 'hidden')}
            >
              Map
            </Button>
          </Col>
          {/** Side List (Medium/Large) */}
          <Col
            id="property-list-container"
            aria-hidden={showPdp || showFilters}
            className={cn(
              'relative basis-1/2 border-t border-od-border-primary bg-od-bg-primary transition-[opacity,visibility] duration-500 max-md:hidden',
              (showPdp || showFilters) && 'invisible opacity-0',
            )}
          >
            <Col className="z-1 h-full w-full overflow-y-scroll px-12 pt-3">
              <QuickFilters
                bedrooms={String(minBeds)}
                bathrooms={String(minBaths)}
                selectedAddress={selectedAddress}
                setSelectedAddress={handleSetSelectedAddress}
                setSelectedLocation={handleSetSelectedLocation}
                selectedLocation={selectedLocation}
                onFilterChange={handleQuickFilterChange}
                isLoadingFilter={isLoading}
                onFiltersButtonClick={onFiltersButtonClick}
                isFocused={isMobileHeaderFocused}
                onApplySavedSearch={applySavedSearch}
                activeSavedSearchId={activeSavedSearch?.id}
              />
              <Text as="p" className="text-body-sm pb-6">
                {propertySearchData?.count} homes for sale near{' '}
                {selectedLocation?.label || marketInfo?.displayName || 'you'}
              </Text>
              <PropertyList
                isLoading={isLoading}
                handleOnSeeMore={loadMore}
                propertyListData={propertyListData}
              />
              {activeMarkets && activeMarkets.length > 0 && !isLoading && (
                <Col className="pt-12 pb-25">
                  <Text as="p" className="text-body-md pb-6 font-medium">
                    Popular Markets
                  </Text>
                  <Grid className="grid-cols-[repeat(auto-fill,minmax(calc(var(--spacing)*62.5),1fr))] gap-4">
                    <Link
                      href="/homes-for-sale-near-me"
                      analyticsName="market-link-near-me"
                      className="block"
                    >
                      <Text as="p" className="text-body-md text-od-content-secondary">
                        Homes for sale near me
                      </Text>
                    </Link>
                    {activeMarkets.map((market) => (
                      <Link
                        key={market.identifier}
                        href={`/homes/${market.identifier}`}
                        analyticsName={`market-link-${market.identifier}`}
                        className="block"
                      >
                        <Text as="p" className="text-body-md text-od-content-secondary">
                          Houses for sale in {market.displayName}
                        </Text>
                      </Link>
                    ))}
                  </Grid>
                </Col>
              )}
            </Col>
          </Col>
          {/** Fullscreen Filters (Small/Medium) */}
          <Col
            id="small-medium-filters"
            aria-hidden={!showFilters}
            className={cn(
              'fixed -top-1.25 z-[15000] h-full w-full items-center bg-od-bg-primary lg:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showFilters && 'invisible -translate-y-[100vh] opacity-0',
            )}
          >
            <MapSearchFilters
              minBeds={toFilterString(minBeds)}
              minBaths={toFilterString(minBaths)}
              canSelfTour={canSelfTour}
              minPrice={dollarToCentsString(minPrice)}
              maxPrice={dollarToCentsString(maxPrice)}
              singleFamily={singleFamily}
              condo={condo}
              townhome={townhome}
              noHoas={noHoas}
              singleStory={singleStory}
              mustHavePool={mustHavePool}
              minYearBuilt={toFilterString(minYearBuilt)}
              maxYearBuilt={toFilterString(maxYearBuilt)}
              minLotSize={toFilterString(minLotSize)}
              maxLotSize={toFilterString(maxLotSize)}
              minSquareFootage={toFilterString(minSquareFootage)}
              maxSquareFootage={toFilterString(maxSquareFootage)}
              handleError={handleError}
              handleMinBedsChange={handleSetBedrooms}
              handleMinBathsChange={handleSetBathrooms}
              onCanSelfTourChange={handleSetCanSelfTour}
              handleMinSquareFootageChange={handleMinSquareFootageChange}
              handleMaxSquareFootageChange={handleMaxSquareFootageChange}
              handleMinPriceChange={handleMinPriceChange}
              handleMaxPriceChange={handleMaxPriceChange}
              handleSingleFamilyChange={handleSingleFamilyChange}
              handleCondoChange={handleCondoChange}
              handleTownhomeChange={handleTownhomeChange}
              handleNoHoasChange={handleNoHoasChange}
              handleSingleStoryChange={handleSingleStoryChange}
              handlePoolChange={handlePoolChange}
              handleMinYearBuiltChange={handleMinYearBuiltChange}
              handleMaxYearBuiltChange={handleMaxYearBuiltChange}
              handleMinLotSizeChange={handleMinLotSizeChange}
              handleMaxLotSizeChange={handleMaxLotSizeChange}
              onApplyFilters={handleMobileFilterClick}
              onResetFilters={handleResetFilters}
              onClose={handleCloseFilters}
              isLoading={isLoading}
              errors={errors}
              saveSearchQuery={getSaveSearchQuery()}
              onApplySavedSearch={applySavedSearch}
              activeSavedSearch={activeSavedSearch}
              locationLabel={selectedLocation?.label}
              onSavedSearchCreated={markActiveSavedSearch}
            />
          </Col>
          {/** Side Filters (Large) */}
          <Col
            id="pdp-filters-container"
            aria-hidden={!showFilters}
            aria-label="Search filters"
            as="aside"
            className={cn(
              'absolute right-0 z-2 h-full w-1/2 border-t border-od-border-primary bg-od-bg-primary max-lg:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showFilters && 'invisible translate-x-[50vh] opacity-0',
            )}
          >
            <MapSearchFilters
              minBeds={toFilterString(minBeds)}
              minBaths={toFilterString(minBaths)}
              canSelfTour={canSelfTour}
              minPrice={dollarToCentsString(minPrice)}
              maxPrice={dollarToCentsString(maxPrice)}
              singleFamily={singleFamily}
              condo={condo}
              townhome={townhome}
              noHoas={noHoas}
              singleStory={singleStory}
              mustHavePool={mustHavePool}
              minYearBuilt={toFilterString(minYearBuilt)}
              maxYearBuilt={toFilterString(maxYearBuilt)}
              minLotSize={toFilterString(minLotSize)}
              maxLotSize={toFilterString(maxLotSize)}
              minSquareFootage={toFilterString(minSquareFootage)}
              maxSquareFootage={toFilterString(maxSquareFootage)}
              handleError={handleError}
              handleMinBedsChange={handleSetBedrooms}
              handleMinBathsChange={handleSetBathrooms}
              onCanSelfTourChange={handleSetCanSelfTour}
              handleMinSquareFootageChange={handleMinSquareFootageChange}
              handleMaxSquareFootageChange={handleMaxSquareFootageChange}
              handleMinPriceChange={handleMinPriceChange}
              handleMaxPriceChange={handleMaxPriceChange}
              handleSingleFamilyChange={handleSingleFamilyChange}
              handleCondoChange={handleCondoChange}
              handleTownhomeChange={handleTownhomeChange}
              handleNoHoasChange={handleNoHoasChange}
              handleSingleStoryChange={handleSingleStoryChange}
              handlePoolChange={handlePoolChange}
              handleMinYearBuiltChange={handleMinYearBuiltChange}
              handleMaxYearBuiltChange={handleMaxYearBuiltChange}
              handleMinLotSizeChange={handleMinLotSizeChange}
              handleMaxLotSizeChange={handleMaxLotSizeChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              onClose={handleCloseFilters}
              isLoading={isLoading}
              errors={errors}
              saveSearchQuery={getSaveSearchQuery()}
              onApplySavedSearch={applySavedSearch}
              activeSavedSearch={activeSavedSearch}
              locationLabel={selectedLocation?.label}
              onSavedSearchCreated={markActiveSavedSearch}
            />
          </Col>
          {/** Mini PDP (Medium/Large) */}
          <Col
            aria-hidden={!showPdp}
            className={cn(
              'absolute right-0 z-2 h-full w-1/2 overflow-y-scroll bg-od-bg-primary max-md:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showPdp && 'invisible translate-x-[50vh] opacity-0',
            )}
          >
            {propertyDetails && (
              <MiniPropertyDetailPage
                property={{
                  ...propertyDetails,
                  isLiked,
                  onClickReaction,
                }}
                onClose={closePropertyDetails}
                key={propertyDetails.addressToken}
              />
            )}
          </Col>
          {/** Mobile Mini PDP (Small) */}
          <Col
            ref={mobileMiniPdpSwipe.panelRef}
            aria-hidden={!showPdp}
            className={cn(
              'absolute bottom-0 left-2.5 right-2.5 z-[3000] overflow-hidden rounded-t-4xl bg-od-bg-primary md:hidden',
              MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
              MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
              !showPdp && cn('invisible opacity-0', MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS),
              mobileMiniPdpSwipe.panelClassName,
            )}
          >
            {propertyDetails && (
              <MobileMiniPropertyDetailPage
                property={propertyDetails}
                onClose={closePropertyDetails}
              />
            )}
          </Col>
        </Col>
        {/* Surfaces genuine (non-superseded) search failures — e.g. an Athena
            rate-limit — so a throttled fetch isn't silently swallowed. */}
        <SearchErrorToast hasError={hasError} clearError={clearError} />
      </Toasty>
    </MarketplaceMapLayout>
  );

  return (
    <>
      <MarketplaceSeoHead
        marketInfo={marketInfo}
        activeMarkets={activeMarkets}
        canonicalOverride={canonicalOverride ?? undefined}
      />
      {RenderBody}
    </>
  );
};

const NOT_FOUND = { notFound: true } as const;

// Map of query params to their canonical path overrides
const CANONICAL_PATH_OVERRIDES: Record<string, string> = {
  is_near_me_page: '/homes-for-sale-near-me',
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  propertyList: MarketplacePropertyListFragment;
  marketInfo: MarketInfo;
  propertySearch: MarketplacePropertySearchFragment;
  queryParams: PropertySearchQuery;
  activeMarkets: ActiveMarket[];
  canonicalOverride: string | null;
  savedSearchId: string | null;
  initialZoneIds: string[] | null;
}> = async (context) => {
  const queryParams = parseQueryParams(context.query);
  const savedSearchId = parseSavedSearchId(context.query);
  const { market, zoneId } = parseMarketParam(queryParams['market']);
  // Match SSR search precedence (vertices > zoneIds): a zid route that also
  // carries a viewport polygon must not seed routeZoneIds, or the controller
  // would force vertices to null on the next client search/save.
  const hasViewportPolygon = (queryParams.vertices?.length ?? 0) > 0;
  let initialZoneIds: string[] | null = null;
  if (!hasViewportPolygon) {
    initialZoneIds = zoneId ? [zoneId] : (queryParams.zoneIds ?? null);
  }

  // Check for canonical path overrides based on query params from cloudflare routing
  const overrideKey = Object.keys(CANONICAL_PATH_OVERRIDES).find(
    (key) => context.query[key] === 'true',
  );
  const canonicalOverride = overrideKey ? CANONICAL_PATH_OVERRIDES[overrideKey] : null;

  let propertyListQuery: GetMarketplacePropertyListQuery | undefined;
  let propertySearchQuery: GetMarketplacePropertySearchQuery | undefined;
  let marketCoordinatesQuery: GetMarketCoordinatesQuery | undefined;
  let zoneQuery: GetZoneDataQuery | undefined;
  let resolvedMarket = market;

  if (market) {
    const res = await tryGetMarketCoordinates(context, market);
    if (res) {
      marketCoordinatesQuery = res.query;
      resolvedMarket = res.resolvedMarket;

      // Redirect to the resolved market if it differs from the requested market
      if (resolvedMarket.toLowerCase() !== market.toLowerCase()) {
        const resolvedIdentifier =
          res.query?.buyer?.getMarketConfigByMarket?.marketConfig?.identifier;
        if (resolvedIdentifier) {
          return {
            redirect: {
              destination: `/homes/${resolvedIdentifier}`,
              permanent: false,
            },
          };
        }
      }
    } else {
      return NOT_FOUND;
    }
  }

  if (zoneId) {
    const res = await tryGetZoneData(context, zoneId);
    if (res) {
      zoneQuery = res;
    } else {
      return NOT_FOUND;
    }
  }

  const propertyListRes = await fetchPropertyList(
    queryParams,
    resolvedMarket,
    zoneId,
    context.req.headers,
  );
  if (propertyListRes) {
    propertyListQuery = propertyListRes;
  } else if (propertyListRes === false) {
    return NOT_FOUND;
  }

  const propertySearchRes = await fetchPropertySearch(
    queryParams,
    resolvedMarket,
    zoneId,
    context.req.headers,
  );
  if (propertySearchRes) {
    propertySearchQuery = propertySearchRes;
  } else if (propertySearchRes === false) {
    return NOT_FOUND;
  }

  const marketInfo = buildMarketInfo(
    zoneQuery,
    marketCoordinatesQuery,
    queryParams,
    resolvedMarket,
  );

  if (!marketInfo) {
    return NOT_FOUND;
  }

  const propertyList = propertyListQuery?.marketplace.propertyList;
  if (!propertyList) {
    return NOT_FOUND;
  }

  const propertySearch = propertySearchQuery?.marketplace.propertySearch;
  if (!propertySearch) {
    return NOT_FOUND;
  }

  const activeMarkets = await fetchActiveBuyMarkets(context.req.headers);

  return {
    props: {
      propertyList,
      marketInfo,
      propertySearch,
      queryParams,
      activeMarkets,
      canonicalOverride,
      savedSearchId,
      initialZoneIds,
    },
  };
};

export default Page;
